import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';

export const FAVORITE_PROJECTS_QUERY_KEY = ['user-favorite-projects'];

export const useFavoriteProjects = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: FAVORITE_PROJECTS_QUERY_KEY,
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<string[]> => {
      if (!user?.id) return [];
      const { data, error } = await supabase
        .from('user_favorite_projects')
        .select('project_id')
        .eq('user_id', user.id);
      if (error) {
        console.error('Error loading favorites', error);
        return [];
      }
      return (data ?? []).map((r) => r.project_id);
    },
  });

  const favoriteIds = query.data ?? [];

  const toggleMutation = useMutation({
    mutationFn: async (projectId: string) => {
      if (!user?.id) throw new Error('Not authenticated');
      const isFav = favoriteIds.includes(projectId);
      if (isFav) {
        const { error } = await supabase
          .from('user_favorite_projects')
          .delete()
          .eq('user_id', user.id)
          .eq('project_id', projectId);
        if (error) throw error;
        return { projectId, added: false };
      }
      const { error } = await supabase
        .from('user_favorite_projects')
        .insert({ user_id: user.id, project_id: projectId });
      if (error) throw error;
      return { projectId, added: true };
    },
    onSuccess: ({ added }) => {
      queryClient.invalidateQueries({ queryKey: FAVORITE_PROJECTS_QUERY_KEY });
      toast({
        title: added ? '⭐ Añadido a favoritos' : 'Quitado de favoritos',
        duration: 1500,
      });
    },
    onError: (e: Error) => {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    },
  });

  return {
    favoriteIds,
    isFavorite: (id: string) => favoriteIds.includes(id),
    toggleFavorite: (id: string) => toggleMutation.mutate(id),
    isLoading: query.isLoading,
  };
};
